import {
  AllQueuesData,
  Appointment,
  QUEUE_TYPE,
  RIGHT_PANEL_VIEW_TYPE,
  WORKFLOW_VIEW_POPUP_STATE,
  SUBJECT_KEYS,
} from "@elixir/types";
import {
  InlineSearchComponentUi,
  MenuItem,
  PrimaryIconButton,
  SortByComponentUi,
  TooltipComponent,
} from "ui";
import { shallow } from "zustand/shallow";
import useAllQueuesQuery from "../../apis/tryst/get-all-queues";
import { ReactComponent as FilterRegularIcon } from "../../assets/icons/font-awesome/filter-regular.svg";
import { ReactComponent as RepeatRegularIcon } from "../../assets/icons/font-awesome/repeat-regular.svg";
import Tabs from "../../molecules/cards/queue/tabs";
import useQueueStore from "../../stores/queue-store";
import { getAptsForDoctor, getCount } from "../../utils/appointment-utils";
import { track } from "../../utils/tracking";
import Filter from "./filter";
import { navigateLocal } from "./queue-header";
import {
  EllipsisRegularIcon,
  PlusRegularIcon,
  TableColumnsRegularIcon,
} from "@elixir/icons";
import { useQueueWorkflowStore } from "@elixir/stores";
import { useEffect, useMemo, useState } from "react";
import { DownloadRegularIcon } from "@elixir/icons";
import {
  getAppointmentToExcelJson,
  queueDataKeysMapSheetName,
} from "../../utils/download-queue-utils";
import useGetAllFiteredQueues from "../../hooks/use-get-all-filtered-queues";
import {
  isAccessAvailable,
  showAccessDeniedError,
} from "../../utils/bridges/methods";
import CustomQueueView from "./custom-queue-view";
import { Dropdown, Menu } from "antd";
import useGetQueueConfig from "../../apis/tryst/get-queue-config";
import { getAclViewClassName } from "@elixir/utils";
import getB from "../../utils/bridges/EkaBridge";
import { getIsEMRPlanExpired } from "../../molecules/tool-payments/common/utils";

export const CREATE_CUSTOM_QUEUE_BUTTON_ID = "create-custom-queue-button";
export const EDIT_QUEUE_BUTTON_ID = "edit-queue-button";

const searchByDefault = new Set([
  "77133577914507",
  "76527282322352",
  "76535964545360",
  "b-161607877174678",
  "7174763797343791",
]);

const RightQueuePanel = () => {
  const { data: queueConfigData } = useGetQueueConfig();
  const { workflow_views = [] } = queueConfigData?.data || {};
  const { data } = useAllQueuesQuery();

  const filterAppliedQueueData = useGetAllFiteredQueues();
  const {
    viewType,
    setViewType,
    rightPanelQueueType,
    setRightPanelQueueType,
    setShowCustomiseYourQueue,
    leftPanelQueueType,
    clinicId,
  } = useQueueStore(
    ({
      leftPanelQueueType,
      viewType,
      setViewType,
      rightPanelQueueType,
      setRightPanelQueueType,
      setShowCustomiseYourQueue,
      clinicId,
    }) => ({
      leftPanelQueueType,
      viewType,
      setViewType,
      rightPanelQueueType,
      setRightPanelQueueType,
      setShowCustomiseYourQueue,
      clinicId,
    }),
    shallow
  );

  const searchQuery = useQueueStore((state) => state.searchQuery);
  const setSearchQuery = useQueueStore((state) => state.setSearchQuery);

  const queueFilter = useQueueStore((state) => state.queueFilter);
  const setQueueFilter = useQueueStore((state) => state.setQueueFilter);
  const setCreateViewPopupState = useQueueWorkflowStore(
    (state) => state.setCreateViewPopupState
  );
  const selectedSort = useQueueStore((state) => state.selectedSort);
  const setSelectedSort = useQueueStore((state) => state.setSelectedSort);

  const selectedDoctor = useQueueStore((state) => state.selectedDoctor);

  const filteredAptData = useMemo(
    () =>
      (Object.keys(data || {}) as (keyof AllQueuesData)[]).reduce(
        (acc, key) => {
          const filteredQueueDataByDoctor = getAptsForDoctor({
            apts: data?.[key] || [],
            doctor_oid: selectedDoctor?.oid || "",
          });
          return {
            ...acc,
            [key]: filteredQueueDataByDoctor,
          };
        },
        {} as AllQueuesData
      ),
    [data, selectedDoctor?.oid]
  );

  const [isAppointmentsExporting, setIsAppointmentsExporting] =
    useState<boolean>(false);

  const downloadAppointments = () => {
    const _data = filterAppliedQueueData;

    setIsAppointmentsExporting(true);

    const wb = window.XLSX.utils.book_new();
    const queueDataKeys = Object.keys(
      _data as AllQueuesData
    ) as (keyof AllQueuesData)[];

    const formattedData = queueDataKeys.map((queueDataKey) => {
      if (!_data[queueDataKey] || (_data[queueDataKey]?.length || 0) === 0)
        return undefined;
      return _data[queueDataKey]?.map((appointment: Appointment) =>
        getAppointmentToExcelJson(appointment, queueConfigData?.data)
      );
    });

    formattedData?.forEach((data, idx) => {
      if (!data) return;
      const viewName = workflow_views.find(
        (view) => view.viewId === queueDataKeys[idx]
      )?.name;
      const sheetName =
        queueDataKeysMapSheetName[queueDataKeys[idx]] || viewName;
      const ws = window.XLSX.utils.json_to_sheet(data);
      window.XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    const filename = `queue.xls`;

    window.XLSX.writeFile(wb, filename);
    setIsAppointmentsExporting(false);
  };

  const handleDownloadQueue = () => {
    if (isAppointmentsExporting) {
      return;
    }
    track("download_queue");
    setIsAppointmentsExporting(true);
    const xlsx = document.createElement("script");
    xlsx.onload = downloadAppointments;
    xlsx.src = "/xlsx-sheet.js";
    document.getElementsByTagName("head")[0].appendChild(xlsx);
  };

  const isDownloadQueueEnabled = isAccessAvailable(SUBJECT_KEYS.DOWNLOAD_QUEUE);

  useEffect(() => {
    const viewConfig = queueConfigData?.data?.workflow_views?.find(
      (item) => item.viewId === rightPanelQueueType
    );

    if (!viewConfig && queueConfigData?.data?.workflow_views?.[0]?.viewId) {
      setRightPanelQueueType(queueConfigData.data.workflow_views[0].viewId);
    }
  }, [queueConfigData?.data?.workflow_views, rightPanelQueueType]);

  const isEMRPlanExpired = getIsEMRPlanExpired();

  return (
    <div className="dr-rounded-t-8 dr-overflow-hidden dr-bg-darwin-touch-neutral-50  dr-h-full dr-relative">
      <Tabs<QUEUE_TYPE.CHECKED_IN | QUEUE_TYPE.COMPLETED | string>
        onChange={(key) => {
          track("right_queue_panel", {
            tab: key,
          });
          setSelectedSort(undefined);
          setRightPanelQueueType(key);
          navigateLocal(clinicId, leftPanelQueueType, key);
        }}
        headerClassName="dr-flex dr-items-center dr-justify-between BodyBody2Regular dr-bg-doctor-darwin-accent-bright-blue-800 dr-h-40 dr-pl-16 dr-overflow-hidden dr-overflow-scroll no-scrollbar"
        headerStyle={
          isEMRPlanExpired
            ? {
                backgroundColor: "#647295",
              }
            : undefined
        }
        headerLeftComponent={<></>}
        headerRightComponent={
          <div
            className="dr-flex dr-items-center dr-space-x-24 BodyBody1Regular dr-text-doctor-darwin-neutral-0 dr-sticky dr-right-0 dr-bg-darwin-touch-primary-primary dr-pr-16"
            style={
              isEMRPlanExpired
                ? {
                    backgroundColor: "#647295",
                  }
                : undefined
            }
          >
            <PrimaryIconButton
              IconComponent={() => (
                <PlusRegularIcon className="dr-w-12 dr-h-14 dr-text-doctor-darwin-neutral-0" />
              )}
              onClick={() => {
                setCreateViewPopupState(WORKFLOW_VIEW_POPUP_STATE.VIEW_NAMING);
              }}
              extraClass={`focus:!dr-outline-none ${getAclViewClassName(
                SUBJECT_KEYS.CREATE_CUSTOM_QUEUE
              )}`}
              style={
                isEMRPlanExpired
                  ? {
                      backgroundColor: "#647295",
                      marginRight: "-16px",
                    }
                  : {
                      marginRight: "-16px",
                    }
              }
              id={CREATE_CUSTOM_QUEUE_BUTTON_ID}
            />
            <InlineSearchComponentUi
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              hideText={true}
              defaultSearch={searchByDefault.has(getB().getMe().user["b-id"])}
            />
            {/* <RefreshQueue
              onClick={async () => {
                const isTodaySelected = dayjs(selectedDate).isSame(
                  dayjs(),
                  "day"
                );

                if (isTodaySelected) {
                  const aptConfig = await getIdb()
                    ?.table("misc")
                    ?.get("aptConfig");

                  await getIdb()?.misc?.put?.({
                    ...aptConfig,
                    key: "aptConfig",
                    config: {
                      ...aptConfig?.config,
                      localAptUpdatedAt: {
                        ...aptConfig?.config?.localAptUpdatedAt,
                        [clinicId]: undefined,
                      },
                    },
                  });
                }

                await queryClient.invalidateQueries(
                  getAllQueuesQuery({
                    startDate: dateFormat(selectedDate, "yyyy-mm-dd"),
                    clinicId: clinicId,
                  })
                );
              }}
            /> */}
            <SortByComponentUi
              selectedSort={selectedSort}
              setSelectedSort={setSelectedSort}
              queueType={rightPanelQueueType}
            />
            <Filter
              queueFilter={queueFilter}
              setQueueFilter={setQueueFilter}
              queueType={rightPanelQueueType}
            >
              <div className="dr-flex dr-items-center dr-space-x-8 dr-cursor-pointer">
                <TooltipComponent title="Filters" placement="bottom">
                  <FilterRegularIcon />
                </TooltipComponent>
              </div>
            </Filter>
            <Dropdown
              className=""
              trigger={["click"]}
              overlay={
                <Menu>
                  <MenuItem
                    icon={
                      <TableColumnsRegularIcon className="dr-w-13 dr-h-14" />
                    }
                    onClick={() =>
                      setCreateViewPopupState(
                        WORKFLOW_VIEW_POPUP_STATE.VIEW_QUEUES
                      )
                    }
                    title="Edit queue"
                    menuItemClassName={getAclViewClassName(
                      SUBJECT_KEYS.EDIT_QUEUE
                    )}
                  />
                  <MenuItem
                    icon={
                      <RepeatRegularIcon className="dr-text-darwin-neutral-800 dr-w-14 dr-h-14" />
                    }
                    onClick={() => {
                      if (viewType === RIGHT_PANEL_VIEW_TYPE.COMPACT) {
                        setViewType(RIGHT_PANEL_VIEW_TYPE.DEFAULT);
                      } else {
                        setViewType(RIGHT_PANEL_VIEW_TYPE.COMPACT);
                      }
                      track("right_queue_panel_view_type", {
                        type:
                          viewType === RIGHT_PANEL_VIEW_TYPE.COMPACT
                            ? RIGHT_PANEL_VIEW_TYPE.DEFAULT
                            : RIGHT_PANEL_VIEW_TYPE.COMPACT,
                      });
                    }}
                    title={
                      viewType === RIGHT_PANEL_VIEW_TYPE.COMPACT
                        ? "Default View"
                        : "Compact View"
                    }
                  />
                  <MenuItem
                    icon={
                      <DownloadRegularIcon className="dr-w-14 dr-h-14 dr-text-darwin-neutral-800" />
                    }
                    onClick={
                      isDownloadQueueEnabled
                        ? handleDownloadQueue
                        : showAccessDeniedError
                    }
                    title="Download Queue"
                    menuItemClassName={getAclViewClassName(
                      SUBJECT_KEYS.DOWNLOAD_QUEUE
                    )}
                  />
                </Menu>
              }
            >
              <div id={EDIT_QUEUE_BUTTON_ID}>
                <EllipsisRegularIcon className="dr-w-14 dr-h-14 dr-rotate-90 dr-text-darwin-touch-neutral-0 dr-cursor-pointer" />
              </div>
            </Dropdown>
          </div>
        }
        className="dr-h-full dr-w-full dr-absolute"
        tabsContainerClassName="space-x-16"
        activeClass="dr-flex dr-items-center dr-justify-center dr-px-8 dr-h-30 dr-rounded-4 dr-border-doctor-darwin-neutral-0 dr-bg-darwin-touch-neutral-0 dr-text-darwin-touch-primary-primary dr-border-transparent TouchFootnoteBold"
        inActiveClass="dr-text-darwin-touch-neutral-0 dr-opacity-70 TouchFootnoteRegular"
        activeKey={rightPanelQueueType}
        items={[
          ...(workflow_views || []).map((view) => {
            return {
              key: view.viewId!,
              label: `${view.name} (${getCount(
                filteredAptData?.[view.viewId!]
              )})`,
              children: <CustomQueueView viewId={view.viewId!} />,
              id: view.viewId,
            };
          }),
        ]}
        transition={false}
      />
    </div>
  );
};

export default RightQueuePanel;
